import type { CalendarEvent } from '../types.js';
import type { EventDragCommit } from '../hooks/useEventDrag.js';
import {
  eventDurationMinutes,
  instantFromZonedWallClock,
  isDateOnlyISO,
  parseISODate,
} from './dateUtils.js';

export interface EventTimePatch {
  readonly startsAt: string;
  readonly endsAt: string | null;
}

export interface ApplyEventDragCommitOptions {
  /** IANA TZ used to resolve day-key boundaries. Required so callers
   *  can't forget — pass the calendar's `viewerTz`; pass `undefined`
   *  only when day keys are already in the system TZ. */
  readonly viewerTz: string | undefined;
  /** Snap granularity — also acts as the resize floor (a resize cannot
   *  shrink an event below this many minutes). Default 15 to match the
   *  `useEventDrag` default. */
  readonly snapMinutes?: number;
}

/**
 * Apply a useEventDrag commit to a CalendarEvent and return the new
 * startsAt / endsAt as ISO strings.
 *
 * - `move`   shifts both startsAt and endsAt by minutesDelta. If the
 *            commit's `targetDayKey` differs from the origin day, the
 *            shift is augmented by the day delta in milliseconds. The
 *            day-key boundaries are resolved in `options.viewerTz` so
 *            cross-TZ usage stays correct across DST jumps.
 * - `resize` shifts endsAt only (start unchanged); endsAt may be
 *            `null` for events without a defined end — in that case we
 *            synthesize one at the dragged delta. The new end is floored
 *            at start + `options.snapMinutes` so the drag can't collapse
 *            the event below the snap granularity.
 *
 * All-day events (date-only ISO) are not handled here — callers should
 * disable drag for those rows; calling this on one returns `null`.
 */
export function applyEventDragCommit(
  event: CalendarEvent,
  commit: EventDragCommit,
  originDayKey: string,
  options: ApplyEventDragCommitOptions,
): EventTimePatch | null {
  if (event.allDay === true || isDateOnlyISO(event.startsAt)) {
    return null;
  }
  const startMs = new Date(event.startsAt).getTime();
  if (Number.isNaN(startMs)) return null;
  const durMin = eventDurationMinutes(event.startsAt, event.endsAt);
  const viewerTz = options.viewerTz;
  const snapMs = (options.snapMinutes ?? 15) * 60_000;

  // Day delta — only meaningful for `move`. Compute via viewer-TZ midnight
  // instants so a cross-day drag picks up the *viewer's* DST offset rather
  // than the system's (which may differ when viewing another TZ).
  let dayDeltaMs = 0;
  if (
    commit.mode === 'move' &&
    commit.targetDayKey !== null &&
    commit.targetDayKey !== originDayKey
  ) {
    const o = parseISODate(originDayKey);
    const t = parseISODate(commit.targetDayKey);
    const od = instantFromZonedWallClock(o.year, o.month, o.day, 0, 0, viewerTz);
    const td = instantFromZonedWallClock(t.year, t.month, t.day, 0, 0, viewerTz);
    dayDeltaMs = td - od;
  }
  const minuteDeltaMs = commit.minutesDelta * 60_000;

  if (commit.mode === 'move') {
    const newStart = new Date(startMs + dayDeltaMs + minuteDeltaMs);
    let newEnd: string | null;
    if (event.endsAt !== null && event.endsAt !== undefined) {
      newEnd = new Date(
        new Date(event.endsAt).getTime() + dayDeltaMs + minuteDeltaMs,
      ).toISOString();
    } else if (durMin !== null) {
      newEnd = new Date(newStart.getTime() + durMin * 60_000).toISOString();
    } else {
      newEnd = null;
    }
    return { startsAt: newStart.toISOString(), endsAt: newEnd };
  }

  // resize: end-only.
  const baseEnd =
    event.endsAt !== null && event.endsAt !== undefined
      ? new Date(event.endsAt).getTime()
      : startMs + (durMin ?? 60) * 60_000;
  let newEndMs = baseEnd + minuteDeltaMs;
  if (newEndMs <= startMs + snapMs) newEndMs = startMs + snapMs;
  return {
    startsAt: event.startsAt,
    endsAt: new Date(newEndMs).toISOString(),
  };
}
